import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = (body) => {
  const isFormData = body instanceof FormData;

  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

export const fetchLeasingMaintenance = createAsyncThunk(
  "leasingMaintenance/fetchAll",
  async ({ page = 1, filters = {} }, { rejectWithValue }) => {
    try {
      let url = `${BASE_URL}/leasing-request/maintenance?page=${page}`;

      if (filters.admin_id) {
        url += `&admin_id=${encodeURIComponent(filters.admin_id)}`;
      }
      if (filters.order_number) {
        url += `&order_number=${encodeURIComponent(filters.order_number)}`;
      }
      if (filters.plate) {
        url += `&plate=${encodeURIComponent(filters.plate)}`;
      }
      if (filters.maintenance_type_id) {
        url += `&maintenance_type_id=${encodeURIComponent(filters.maintenance_type_id)}`;
      }
      if (filters.status) {
        url += `&status=${encodeURIComponent(filters.status)}`;
      }

      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load maintenance list";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingMaintenanceRecord = createAsyncThunk(
  "leasingMaintenance/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/leasing-request/maintenance/${id}`, {
        headers: getHeaders(),
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load maintenance record";
      return rejectWithValue(message);
    }
  }
);

const appendMaintenanceFormData = (formData) => {
  const data = new FormData();
  if (formData.admin_id) data.append("admin_id", formData.admin_id);
  if (formData.maintenance_date) data.append("maintenance_date", formData.maintenance_date);
  if (formData.vehicle_id) data.append("vehicle_id", formData.vehicle_id);
  if (formData.maintenance_type_id) data.append("maintenance_type_id", formData.maintenance_type_id);
  if (formData.meter_reading !== undefined && formData.meter_reading !== null) {
    data.append("meter_reading", formData.meter_reading);
  }
  if (formData.status) data.append("status", formData.status);
  if (formData.cost !== undefined && formData.cost !== null) {
    data.append("cost", formData.cost);
  }
  if (formData.notes) data.append("notes", formData.notes);

  if (formData.attachments && formData.attachments.length > 0) {
    formData.attachments.forEach((file) => {
      data.append("attachments[]", file);
    });
  }

  return data;
};

export const addLeasingMaintenance = createAsyncThunk(
  "leasingMaintenance/add",
  async (formData, { rejectWithValue }) => {
    try {
      const data = appendMaintenanceFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/maintenance`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add maintenance request";
      return rejectWithValue(message);
    }
  }
);

export const updateLeasingMaintenance = createAsyncThunk(
  "leasingMaintenance/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data = appendMaintenanceFormData(formData);
      const response = await axios.post(`${BASE_URL}/leasing-request/maintenance/${id}`, data, {
        headers: getHeaders(data),
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update maintenance request";
      return rejectWithValue(message);
    }
  }
);

export const deleteLeasingMaintenance = createAsyncThunk(
  "leasingMaintenance/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/leasing-request/maintenance/${id}`, {
        headers: getHeaders(),
      });
      return { id, message: response.data.message || "Deleted successfully" };
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete maintenance request";
      return rejectWithValue(message);
    }
  }
);

export const fetchMaintenanceTypes = createAsyncThunk(
  "leasingMaintenance/fetchMaintenanceTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/maintenance-types`, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load maintenance types";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingEmployees = createAsyncThunk(
  "leasingMaintenance/fetchEmployees",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/employees?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/employees`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load employees";
      return rejectWithValue(message);
    }
  }
);

export const fetchLeasingVehicles = createAsyncThunk(
  "leasingMaintenance/fetchVehicles",
  async (search = "", { rejectWithValue }) => {
    try {
      const url = search
        ? `${BASE_URL}/settings/vehicles?search=${encodeURIComponent(search)}`
        : `${BASE_URL}/settings/vehicles`;
      const response = await axios.get(url, {
        headers: getHeaders(),
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load vehicles";
      return rejectWithValue(message);
    }
  }
);

const LeasingMaintenanceSlice = createSlice({
  name: "leasingMaintenance",
  initialState: {
    maintenance: [],
    maintenanceRecord: null,
    pagination: null,
    maintenanceTypes: [],
    employees: [],
    vehicles: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeasingMaintenance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingMaintenance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.maintenance = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchLeasingMaintenance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchLeasingMaintenanceRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeasingMaintenanceRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.maintenanceRecord = action.payload;
      })
      .addCase(fetchLeasingMaintenanceRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLeasingMaintenance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLeasingMaintenance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Added successfully";
      })
      .addCase(addLeasingMaintenance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLeasingMaintenance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeasingMaintenance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "Updated successfully";
      })
      .addCase(updateLeasingMaintenance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteLeasingMaintenance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLeasingMaintenance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.maintenance = state.maintenance.filter(
          (item) => item.id !== action.payload.id
        );
        state.success = action.payload.message || "Deleted successfully";
      })
      .addCase(deleteLeasingMaintenance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchMaintenanceTypes.fulfilled, (state, action) => {
        state.maintenanceTypes = action.payload;
      })
      .addCase(fetchLeasingEmployees.fulfilled, (state, action) => {
        state.employees = action.payload;
      })
      .addCase(fetchLeasingVehicles.fulfilled, (state, action) => {
        state.vehicles = action.payload;
      });
  },
});

export const { clearState } = LeasingMaintenanceSlice.actions;
export default LeasingMaintenanceSlice.reducer;

